import React, { useState, useEffect } from 'react';
import './PrioritySliders.css';

interface PriorityValues {
  speed: number;
  budget: number;
  quality: number;
}

interface PrioritySliderProps {
  onChange: (values: PriorityValues) => void;
  initialValues?: PriorityValues;
}

const PrioritySliders: React.FC<PrioritySliderProps> = ({ 
  onChange, 
  initialValues = { speed: 33, budget: 33, quality: 34 } 
}) => {
  const [values, setValues] = useState<PriorityValues>(initialValues);

  const handleSliderChange = (type: keyof PriorityValues, newValue: number) => {
    // Ensure the value is between 0 and 100
    const clampedValue = Math.max(0, Math.min(100, newValue));
    
    // Calculate remaining value to distribute between other two
    const remaining = 100 - clampedValue;
    
    let newValues: PriorityValues;
    
    if (type === 'speed') {
      // Distribute remaining between budget and quality proportionally
      const currentTotal = values.budget + values.quality;
      if (currentTotal === 0) {
        newValues = {
          speed: clampedValue,
          budget: remaining / 2,
          quality: remaining / 2
        };
      } else {
        const budgetRatio = values.budget / currentTotal;
        const qualityRatio = values.quality / currentTotal;
        newValues = {
          speed: clampedValue,
          budget: remaining * budgetRatio,
          quality: remaining * qualityRatio
        };
      }
    } else if (type === 'budget') {
      const currentTotal = values.speed + values.quality;
      if (currentTotal === 0) {
        newValues = {
          speed: remaining / 2,
          budget: clampedValue,
          quality: remaining / 2
        };
      } else {
        const speedRatio = values.speed / currentTotal;
        const qualityRatio = values.quality / currentTotal;
        newValues = {
          speed: remaining * speedRatio,
          budget: clampedValue,
          quality: remaining * qualityRatio
        };
      }
    } else { // quality
      const currentTotal = values.speed + values.budget;
      if (currentTotal === 0) {
        newValues = {
          speed: remaining / 2,
          budget: remaining / 2,
          quality: clampedValue
        };
      } else {
        const speedRatio = values.speed / currentTotal;
        const budgetRatio = values.budget / currentTotal;
        newValues = {
          speed: remaining * speedRatio,
          budget: remaining * budgetRatio,
          quality: clampedValue
        };
      }
    }

    // Round values and ensure they total exactly 100
    const rounded = {
      speed: Math.round(newValues.speed),
      budget: Math.round(newValues.budget),
      quality: Math.round(newValues.quality)
    };

    // Adjust for rounding errors
    const total = rounded.speed + rounded.budget + rounded.quality;
    if (total !== 100) {
      const diff = 100 - total;
      // Add the difference to the largest value
      const largest = Object.keys(rounded).reduce((a, b) => 
        rounded[a as keyof PriorityValues] > rounded[b as keyof PriorityValues] ? a : b
      ) as keyof PriorityValues;
      rounded[largest] += diff;
    }

    setValues(rounded);
    onChange(rounded);
  };

  useEffect(() => {
    onChange(values);
  }, [onChange, values]);

  return (
    <div className="priority-sliders-container">
      <div className="sliders-wrapper">
        <div className="slider-group">
          <label className="slider-label">Speed</label>
          <div className="slider-container">
            <input
              type="range"
              min="0"
              max="100"
              value={values.speed}
              onChange={(e) => handleSliderChange('speed', parseInt(e.target.value))}
              className="priority-slider speed-slider"
            />
            <div className="slider-value">{values.speed}%</div>
          </div>
          <div className="priority-description">
            <div className="priority-scale">
              <span>0% = Don't care about speed</span>
              <span>100% = Speed is everything</span>
            </div>
          </div>
        </div>

        <div className="slider-group">
          <label className="slider-label">Budget</label>
          <div className="slider-container">
            <input
              type="range"
              min="0"
              max="100"
              value={values.budget}
              onChange={(e) => handleSliderChange('budget', parseInt(e.target.value))}
              className="priority-slider budget-slider"
            />
            <div className="slider-value">{values.budget}%</div>
          </div>
          <div className="priority-description">
            <div className="priority-scale">
              <span>0% = Don't care about budget</span>
              <span>100% = Budget is all that matters</span>
            </div>
          </div>
        </div>

        <div className="slider-group">
          <label className="slider-label">Quality</label>
          <div className="slider-container">
            <input
              type="range"
              min="0"
              max="100"
              value={values.quality}
              onChange={(e) => handleSliderChange('quality', parseInt(e.target.value))}
              className="priority-slider quality-slider"
            />
            <div className="slider-value">{values.quality}%</div>
          </div>
          <div className="priority-description">
            <div className="priority-scale">
              <span>0% = Don't care about quality</span>
              <span>100% = Quality is everything</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="total-display">
        Total: {values.speed + values.budget + values.quality}%
        {values.speed + values.budget + values.quality !== 100 && (
          <span className="total-warning"> (Must equal 100%)</span>
        )}
      </div>
    </div>
  );
};

export default PrioritySliders; 